<script setup lang="ts">
import { Form } from '@primevue/forms';
import { type FormSubmitEvent } from '@primevue/forms/form';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { CircleCheck, CircleX, LoaderCircle, Send } from 'lucide-vue-next';
import { FloatLabel, InputText } from 'primevue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';
import { forgottenPassword as forgottenPasswordRequest } from '@api/forgotten-password';

const { t } = useI18n();

const requestLoading = ref<boolean>(false);
const requestFailed = ref<boolean>(false);
const requestSuccess = ref<boolean>(false);

const onFormSubmit = async (event: FormSubmitEvent) => {
    if (!event.valid) return;

    requestLoading.value = true;

    const result = await forgottenPasswordRequest(event.values.email);

    requestLoading.value = false;
    requestFailed.value = !result;
    requestSuccess.value = result;
};

const resolver = zodResolver(
    z.object({
        email: z
            .string({ message: t('error.requiredField') })
            .trim()
            .nonempty({ message: t('error.requiredField') })
            .pipe(
                z.email({
                    message: t('error.invalidEmailAddress'),
                    pattern: z.regexes.unicodeEmail
                })
            )
    })
);
</script>

<template>
    <Form
        v-slot="$form"
        :aria-label="$t('ariaLabel.forgottenPasswordForm')"
        class="flex flex-col gap-4"
        :resolver="resolver"
        @submit="onFormSubmit">
        <div v-if="!requestSuccess && !requestFailed" class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <InputText
                    autocomplete="username"
                    :disabled="requestLoading"
                    inputId="email"
                    name="email"
                    type="text"
                    fluid />
                <label for="email">{{ $t('email') }}</label>
            </FloatLabel>
            <Message
                v-if="$form.email?.invalid"
                :aria-label="$t('ariaLabel.errorMessage')"
                severity="error"
                size="small"
                variant="simple"
                >{{ $form.email.error.message }}</Message
            >
        </div>
        <Message v-else-if="requestSuccess && !requestFailed" severity="success">
            <template #icon><CircleCheck :size="32" /></template>
            <template #default>
                {{ $t('forgottenPasswordRequestSent') }}
            </template>
        </Message>
        <Message v-else severity="error">
            <template #icon><CircleX :size="32" /></template>
            <template #default>
                {{ $t('error.somethingWentWrong') }}
                <br />
                {{ $t('error.pleaseTryAgain') }}
            </template>
        </Message>
        <div class="flex flex-col-reverse md:flex-row gap-4 justify-between">
            <Button
                v-if="!requestFailed"
                :as="requestLoading ? 'button' : 'a'"
                :aria-label="$t('ariaLabel.backToLoginButton')"
                :disabled="requestLoading"
                class="w-full md:w-auto"
                href="#login"
                severity="secondary"
                size="small"
                :label="$t('backToLogin')"
                variant="text" />
            <Button
                v-else
                @click="requestFailed = false"
                class="w-full md:w-auto"
                severity="secondary"
                size="small"
                :label="$t('tryAgain')"
                variant="text" />
            <Button
                v-if="!requestSuccess && !requestFailed"
                :aria-label="$t('ariaLabel.submitButton')"
                :disabled="requestLoading"
                type="submit"
                :label="$t('submit')">
                <template #icon>
                    <LoaderCircle v-if="requestLoading" class="animate-spin" />
                    <Send v-else />
                </template>
            </Button>
        </div>
    </Form>
</template>
